// src/pages/UserTasksPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTasks } from "../context/TasksContext";

const doneLabels = [
  "Hotovo ✔",
  "Vyřízeno 🙌",
  "Splněno ✅",
  "Dokončit 🎯",
  "Mám to! 🎉",
  "Ukončit 🏁",
  "Zvládnuto 💪",
  "Vyřízeno 📌",
  "Odfajfknuto ☑️",
  "Vyřízeno 📨",
  "Jde to pryč 🧹",
  "Splněno na 100% 🔥",
  "Úkol pryč 🧾",
  "Mise splněna 🕹️",
  "Za mnou ✔",
];

function hashCode(str) {
  return str.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
}

function labelForTask(t, done) {
  if (done) return "Vrátit zpět";
  const idx = Math.abs(hashCode(t._id)) % doneLabels.length;
  return doneLabels[idx];
}

export default function UserTasksPage() {
  const { user } = useAuth();
  const tasksCtx = useTasks();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [tab, setTab] = useState("active");
  const [showCompletedInAll, setShowCompletedInAll] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDueDate, setEditDueDate] = useState(""); // yyyy-mm-dd
  const [editRemindAt, setEditRemindAt] = useState(""); // yyyy-mm-ddThh:mm
  const [editSaving, setEditSaving] = useState(false);

  const [snoozingId, setSnoozingId] = useState(null);

  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [hasAutoOpenedReminders, setHasAutoOpenedReminders] = useState(false);

  // ---------------------------------------------------------
  // Určení klíče pro completions
  // ---------------------------------------------------------
  const myKey = user?.pharmacyCode
    ? String(user.pharmacyCode).trim() // NEODSTRANĚVAT nuly – backend ukládá přesně
    : user?._id
    ? String(user._id)
    : null;

  // pokud není k dispozici ani pharmacyCode ani userId → není jak zobrazit úkoly
  const canIdentify = Boolean(myKey);

  // ---------------------------------------------------------
  // FETCH TASKS
  // ---------------------------------------------------------
  const fetchTasks = async () => {
    if (!canIdentify) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setBanner(null);

    try {
      const res = await fetch("/api/tasks/my", {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json().catch(() => []);

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setTasks(Array.isArray(data) ? data : []);

      tasksCtx?.refresh?.();
    } catch (e) {
      console.error("fetchTasks error:", e);
      setBanner({
        type: "error",
        msg: `Chyba při načítání úkolů: ${e.message}`,
      });
      setTasks([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------
  const isDone = (t) => !!t?.completions?.[myKey]?.done;

  const fmtDate = (d) => {
    if (!d) return "Bez termínu";
    const dt = new Date(d);
    return isNaN(dt) ? "Bez termínu" : dt.toLocaleDateString("cs-CZ");
  };

  const fmtDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? "—" : dt.toLocaleString("cs-CZ").replace(/\. /g, ".");
  };

  const getEffectiveDate = (t) => {
    const snoozeUntil = myKey ? t?.snoozes?.[myKey]?.until : null;
    return snoozeUntil || t?.remindAt || t?.dueDate || null;
  };

  const hasReminder = (t) => !!t?.remindAt;
  const isSnoozed = (t) => !!(myKey && t?.snoozes?.[myKey]?.until);

  const isReminderActive = (t) => {
    if (!t?.remindAt) return false;
    if (t?.status === "done" || t?.status === "archived") return false;

    const remindAt = new Date(t.remindAt);
    if (isNaN(remindAt.getTime())) return false;

    const snoozeUntil = myKey ? t?.snoozes?.[myKey]?.until : null;
    if (snoozeUntil) {
      const snoozeDate = new Date(snoozeUntil);
      if (!isNaN(snoozeDate.getTime()) && snoozeDate > new Date()) {
        return false;
      }
    }

    return remindAt <= new Date();
  };

  const activeReminders = useMemo(() => {
    return tasks.filter((t) => isReminderActive(t) && !isDone(t));
  }, [tasks, myKey]);

  useEffect(() => {
    if (!loading && !hasAutoOpenedReminders && activeReminders.length > 0) {
      setReminderModalOpen(true);
      setHasAutoOpenedReminders(true);
    }
  }, [loading, hasAutoOpenedReminders, activeReminders.length]);

  // ---------------------------------------------------------
  // Toggle kompletace
  // ---------------------------------------------------------
  const toggle = async (task) => {
    const currentlyDone = isDone(task);
    const newDone = !currentlyDone;

    // Optimistická aktualizace
    setTasks((prev) =>
      prev.map((t) =>
        t._id === task._id
          ? {
              ...t,
              completions: {
                ...t.completions,
                [myKey]: {
                  done: newDone,
                  doneAt: newDone ? new Date().toISOString() : null,
                  doneBy: user?._id || null,
                },
              },
            }
          : t
      )
    );

    try {
      const res = await fetch(`/api/tasks/${task._id}/complete`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ done: newDone }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      tasksCtx?.refresh?.();
    } catch (e) {
      console.error("toggle error:", e);

      setBanner({
        type: "error",
        msg: `Nepodařilo se změnit stav: ${e.message}`,
      });

      // revert změny
      setTasks((prev) =>
        prev.map((t) =>
          t._id === task._id
            ? {
                ...t,
                completions: {
                  ...t.completions,
                  [myKey]: {
                    done: currentlyDone,
                    doneAt: currentlyDone ? new Date().toISOString() : null,
                    doneBy: user?._id || null,
                  },
                },
              }
            : t
        )
      );
    }
  };

  // ---------------------------------------------------------
  // Třídění úkolů
  // ---------------------------------------------------------
  const sortedTasks = useMemo(() => {
    const ts = (t) => {
      const d = getEffectiveDate(t);
      return d ? new Date(d).getTime() : Infinity;
    };

    return [...tasks].sort((a, b) => ts(a) - ts(b));
  }, [tasks, myKey]);

  const activeTasks = useMemo(
    () => sortedTasks.filter((t) => !isDone(t)),
    [sortedTasks]
  );

  const completedTasks = useMemo(
    () => sortedTasks.filter((t) => isDone(t)),
    [sortedTasks]
  );

  const visible =
    tab === "active"
      ? activeTasks
      : tab === "completed"
      ? completedTasks
      : sortedTasks;

  // ---------------------------------------------------------
  // Snooze / edit
  // ---------------------------------------------------------
  const applySnooze = async (task, untilIso) => {
    try {
      setSnoozingId(task._id);
      setBanner(null);

      const res = await fetch(`/api/tasks/${task._id}/snooze`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ until: untilIso }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setTasks((prev) => prev.map((t) => (t._id === data._id ? data : t)));

      setEditTask((prev) => {
        if (!prev) return prev;
        return prev._id === data._id ? data : prev;
      });

      tasksCtx?.refresh?.();
      return data;
    } catch (e) {
      setBanner({
        type: "error",
        msg: `Odložení se nezdařilo: ${e.message}`,
      });
      return null;
    } finally {
      setSnoozingId(null);
    }
  };

  const snoozeTask = async (task, amount, unit = "days") => {
    const baseSource =
      (myKey && task?.snoozes?.[myKey]?.until) ||
      task?.remindAt ||
      new Date().toISOString();

    let base = new Date(baseSource);
    const now = new Date();

    // 🔥 KLÍČOVÁ OPRAVA
    // pokud je base v minulosti → vezmi teď
    if (isNaN(base.getTime()) || base < now) {
      base = now;
    }

    if (unit === "minutes") {
      base.setMinutes(base.getMinutes() + amount);
    } else {
      base.setDate(base.getDate() + amount);
    }

    const updated = await applySnooze(task, base.toISOString());

    if (updated) {
      const remaining = tasks
        .map((t) => (t._id === updated._id ? updated : t))
        .filter((t) => isReminderActive(t) && !isDone(t));

      if (remaining.length === 0) {
        setReminderModalOpen(false);
      }
    }

    return updated;
  };

  const clearSnooze = async (task) => {
    return applySnooze(task, null);
  };

  const openEdit = (t) => {
    setEditTask(t);
    setEditTitle(t?.title || "");
    setEditDescription(t?.description || "");

    const due = t?.dueDate ? new Date(t.dueDate) : null;
    const dueIso = due && !isNaN(due) ? due.toISOString().slice(0, 10) : "";
    setEditDueDate(dueIso);

    const rem = t?.remindAt ? new Date(t.remindAt) : null;
    const remIso =
      rem && !isNaN(rem)
        ? new Date(rem.getTime() - rem.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16)
        : "";
    setEditRemindAt(remIso);

    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editTask?._id) return;

    try {
      setEditSaving(true);
      setBanner(null);

      const payload = {
        title: editTitle,
        description: editDescription,
        dueDate: editDueDate ? `${editDueDate}T00:00:00.000Z` : null,
        remindAt: editRemindAt ? `${editRemindAt}:00` : null,
      };

      const res = await fetch(`/api/tasks/my/${editTask._id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setTasks((prev) => prev.map((t) => (t._id === data._id ? data : t)));
      setEditTask(data);

      tasksCtx?.refresh?.();
      setEditOpen(false);
      setEditTask(null);
    } catch (e) {
      setBanner({ type: "error", msg: `Uložení se nezdařilo: ${e.message}` });
    } finally {
      setEditSaving(false);
    }
  };

  // ---------------------------------------------------------
  // Render jedné úlohy
  // ---------------------------------------------------------
  const renderTask = (t, allowToggle = true) => {
    const done = isDone(t);
    const doneAt = t?.completions?.[myKey]?.doneAt;

    return (
      <div
        key={t._id}
        className={`flex items-start gap-6 p-4 rounded-xl border transition-all ${
          done
            ? "bg-green-50 border-green-300 scale-[1.02]"
            : "bg-white border-gray-200 hover:bg-gray-50"
        }`}
      >
        <label className="relative flex items-center">
          <input
            type="checkbox"
            checked={done}
            onChange={() => allowToggle && toggle(t)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 flex items-center justify-center rounded border transition-all ${
              done
                ? "bg-green-600 border-green-700 scale-110"
                : "bg-white border-gray-300 hover:border-gray-400"
            }`}
          >
            {done && (
              <svg
                className="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
        </label>

        <div className="flex-1">
          <div className={`font-medium ${done ? "line-through text-gray-500" : ""}`}>
            {isSnoozed(t) ? "⏰ " : hasReminder(t) ? "🔔 " : ""}
            {t.title}
          </div>

          {t.description && (
            <div className={`text-sm opacity-80 ${done ? "line-through" : ""}`}>
              {t.description}
            </div>
          )}

          <div className="text-xs mt-1 text-gray-500">
            Termín splnění: {fmtDate(t.dueDate)}
            {t.remindAt && <> · Připomenutí: {fmtDateTime(t.remindAt)}</>}
            {isSnoozed(t) && (
              <> · Odloženo do: {fmtDateTime(t?.snoozes?.[myKey]?.until)}</>
            )}
            {done && doneAt && <> · Splněno: {fmtDate(doneAt)}</>}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {allowToggle && (
            <button
              onClick={() => toggle(t)}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                done
                  ? "bg-green-600 text-white"
                  : "border-gray-300 hover:bg-gray-100"
              }`}
            >
              {labelForTask(t, done)}
            </button>
          )}

          {!done && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => snoozeTask(t, 15, "minutes")}
                disabled={snoozingId === t._id}
                className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
              >
                +15 min
              </button>
              <button
                onClick={() => snoozeTask(t, 1, "days")}
                disabled={snoozingId === t._id}
                className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
              >
                +1 den
              </button>
              <button
                onClick={() => snoozeTask(t, 7, "days")}
                disabled={snoozingId === t._id}
                className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
              >
                +7 dní
              </button>
            </div>
          )}

          {String(t?.createdBy || "") === String(user?._id || "") && (
            <button
              onClick={() => openEdit(t)}
              className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
            >
              Upravit
            </button>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------
  // Render stránky
  // ---------------------------------------------------------
  return (
    <div className="p-6 space-y-4">
      {banner && (
        <div className="p-3 rounded-lg border bg-red-50 border-red-300 text-red-800 flex justify-between">
          <div className="text-sm">{banner.msg}</div>
          <button
            className="text-xs underline"
            onClick={() => setBanner(null)}
          >
            zavřít
          </button>
        </div>
      )}

      {!loading && activeReminders.length > 0 && (
        <div className="p-3 rounded-lg border bg-blue-50 border-blue-300 text-blue-900 flex justify-between items-center gap-3">
          <div className="text-sm">
            Máte {activeReminders.length} aktivní připomenutí.
          </div>
          <button
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            onClick={() => setReminderModalOpen(true)}
          >
            Zobrazit
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl font-semibold">Moje úkoly</h1>

        <div className="flex-1 flex justify-center">
          <div className="flex gap-2 bg-gray-100 rounded-xl p-1">
            {["active", "completed", "all"].map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-4 py-1.5 rounded-lg text-sm ${
                  tab === k
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                {k === "active"
                  ? "Aktivní"
                  : k === "completed"
                  ? "Hotové"
                  : "Vše"}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={fetchTasks}
          className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm"
        >
          Obnovit
        </button>
      </div>

      {loading ? (
        <div>Načítám…</div>
      ) : (
        <>
          {tab !== "all" ? (
            <div className="space-y-3">
              {visible.map((t) => renderTask(t))}
              {visible.length === 0 && (
                <div className="text-sm opacity-80">Nic k zobrazení.</div>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {activeTasks.map((t) => renderTask(t))}
                {activeTasks.length === 0 && (
                  <div className="text-sm opacity-80">Žádné aktivní úkoly.</div>
                )}
              </div>

              <div className="my-6 border-t" />

              <div className="flex items-center justify-between">
                <div className="font-medium">Hotové ({completedTasks.length})</div>
                <button
                  className="text-sm underline"
                  onClick={() => setShowCompletedInAll((v) => !v)}
                >
                  {showCompletedInAll ? "skrýt" : "zobrazit"}
                </button>
              </div>

              {showCompletedInAll && (
                <div className="mt-2 space-y-3">
                  {completedTasks.map((t) => renderTask(t, true))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {reminderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-lg font-semibold">Aktivní připomenutí</h3>
              <button
                onClick={() => setReminderModalOpen(false)}
                className="text-sm underline"
              >
                zavřít
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              {activeReminders.length === 0 ? (
                <div className="text-sm text-gray-500">
                  Žádná aktivní připomenutí.
                </div>
              ) : (
                activeReminders.map((t) => (
                  <div
                    key={t._id}
                    className="border rounded-xl p-4 bg-blue-50/40"
                  >
                    <div className="font-medium mb-1">🔔 {t.title}</div>

                    {t.description && (
                      <div className="text-sm text-gray-700 mb-2">
                        {t.description}
                      </div>
                    )}

                    <div className="text-xs text-gray-600 mb-3">
                      Termín splnění: {fmtDate(t.dueDate)}
                      {t.remindAt && <> · Připomenutí: {fmtDateTime(t.remindAt)}</>}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => toggle(t)}
                        className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm"
                      >
                        Označit hotovo
                      </button>

                      <button
                        onClick={() => snoozeTask(t, 15, "minutes")}
                        disabled={snoozingId === t._id}
                        className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                      >
                        Odložit o 15 min
                      </button>

                      <button
                        onClick={() => snoozeTask(t, 1, "days")}
                        disabled={snoozingId === t._id}
                        className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                      >
                        Odložit o 1 den
                      </button>

                      <button
                        onClick={() => snoozeTask(t, 7, "days")}
                        disabled={snoozingId === t._id}
                        className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                      >
                        Odložit o 7 dní
                      </button>

                      {String(t?.createdBy || "") === String(user?._id || "") && (
                        <button
                          onClick={() => {
                            setReminderModalOpen(false);
                            openEdit(t);
                          }}
                          className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                        >
                          Upravit
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-semibold">Upravit úkol</div>
              <button
                className="text-sm underline"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
              >
                zavřít
              </button>
            </div>

            <div className="space-y-3">
              <input
                className="w-full border rounded px-3 py-2"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Název"
              />

              <textarea
                className="w-full border rounded px-3 py-2 h-24"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Popis"
              />

              <div>
                <label className="block text-xs text-gray-600 mb-1">Termín</label>
                <input
                  type="date"
                  className="border rounded px-3 py-2"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Připomenout
                </label>
                <input
                  type="datetime-local"
                  className="w-full border rounded px-3 py-2"
                  value={editRemindAt}
                  onChange={(e) => setEditRemindAt(e.target.value)}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  Toto je původní čas připomenutí. Odložení se ukládá zvlášť.
                </div>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                <div>
                  <span className="text-gray-500">Původní připomenutí:</span>{" "}
                  <span className="font-medium">
                    {editTask?.remindAt ? fmtDateTime(editTask.remindAt) : "nenastaveno"}
                  </span>
                </div>

                <div className="mt-1">
                  <span className="text-gray-500">Odloženo do:</span>{" "}
                  <span className="font-medium">
                    {editTask?.snoozes?.[myKey]?.until
                      ? fmtDateTime(editTask?.snoozes?.[myKey]?.until)
                      : "bez odložení"}
                  </span>
                </div>

                {editTask?.snoozes?.[myKey]?.until && (
                  <button
                    onClick={() => clearSnooze(editTask)}
                    disabled={snoozingId === editTask?._id}
                    className="mt-3 px-3 py-2 rounded-lg border text-sm hover:bg-white disabled:opacity-60"
                  >
                    Zrušit odložení
                  </button>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 rounded border hover:bg-gray-50"
                  onClick={() => setEditOpen(false)}
                  disabled={editSaving}
                >
                  Zrušit
                </button>
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  onClick={saveEdit}
                  disabled={editSaving || !editTitle.trim()}
                >
                  {editSaving ? "Ukládám…" : "Uložit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}